import type { SupabaseClient } from '@supabase/supabase-js';
import type { Habit, HabitLog } from '@/types/types';
import { getLocalDateString } from './shared';

export async function getAllHabits(client: SupabaseClient, userId: string): Promise<Habit[]> {
    const { data } = await client.from('habits').select('*').eq('user_id', userId);
    return data || [];
}

export async function getAllHabitLogs(client: SupabaseClient, userId: string): Promise<HabitLog[]> {
    const { data } = await client.from('habit_logs').select('*').eq('user_id', userId);
    return data || [];
}

export async function addHabit(client: SupabaseClient, userId: string, habit: Habit): Promise<void> {
    const { error } = await client.from('habits').insert({
        id: habit.id,
        user_id: userId,
        cycle_id: habit.cycle_id,
        name: habit.name,
        weight: habit.weight,
        category: habit.category,
        linked_goal_id: habit.linked_goal_id,
        sort_order: habit.sort_order ?? 0
    });
    if (error) throw error;
}

export async function updateHabit(client: SupabaseClient, userId: string, habit: Habit): Promise<void> {
    const { error } = await client.from('habits').update({
        name: habit.name,
        weight: habit.weight,
        category: habit.category,
        linked_goal_id: habit.linked_goal_id,
        sort_order: habit.sort_order
    }).eq('id', habit.id).eq('user_id', userId);
    if (error) throw error;
}

export async function deleteHabit(client: SupabaseClient, userId: string, habitId: string): Promise<void> {
    const { error } = await client.from('habits').delete().eq('id', habitId).eq('user_id', userId);
    if (error) throw error;
}

export async function toggleHabit(client: SupabaseClient, userId: string, habitId: string, isChecked: boolean): Promise<void> {
    const todayStr = getLocalDateString();

    if (isChecked) {
        // Idempotent: ON CONFLICT DO NOTHING against the UNIQUE (habit_id, date)
        // constraint. .select() returns rows only when a NEW row was inserted,
        // so a repeat check is a safe no-op (no duplicate-key error, no double streak).
        const { data: inserted, error } = await client.from('habit_logs')
            .upsert(
                { habit_id: habitId, date: todayStr, status: true, user_id: userId },
                { onConflict: 'habit_id,date', ignoreDuplicates: true },
            )
            .select('id');
        if (error) throw error;

        if (inserted && inserted.length > 0) {
            const { data: habit } = await client.from('habits').select('linked_goal_id').eq('id', habitId).eq('user_id', userId).single();
            if (habit?.linked_goal_id) {
                await client.rpc('increment_goal_streak', { p_goal_id: habit.linked_goal_id, p_user_id: userId, p_delta: 1 });
            }
        }
    } else {
        // Idempotent: deleting an absent row affects nothing. .select() returns
        // the deleted rows, so we only adjust the streak when a row actually left.
        const { data: deleted, error } = await client.from('habit_logs').delete()
            .eq('habit_id', habitId)
            .eq('date', todayStr)
            .eq('user_id', userId)
            .select('id');
        if (error) throw error;

        if (deleted && deleted.length > 0) {
            const { data: habit } = await client.from('habits').select('linked_goal_id').eq('id', habitId).eq('user_id', userId).single();
            if (habit?.linked_goal_id) {
                await client.rpc('increment_goal_streak', { p_goal_id: habit.linked_goal_id, p_user_id: userId, p_delta: -1 });
            }
        }
    }
}

export async function toggleHabitForDate(client: SupabaseClient, userId: string, habitId: string, date: string, isChecked: boolean): Promise<void> {
    if (isChecked) {
        // Idempotent against UNIQUE (habit_id, date): a repeat check is a no-op.
        const { error } = await client.from('habit_logs')
            .upsert(
                { habit_id: habitId, date, status: true, user_id: userId },
                { onConflict: 'habit_id,date', ignoreDuplicates: true },
            );
        if (error) throw error;
    } else {
        const { error } = await client.from('habit_logs').delete()
            .eq('habit_id', habitId)
            .eq('date', date)
            .eq('user_id', userId);
        if (error) throw error;
    }
}

export async function reorderHabits(client: SupabaseClient, userId: string, orderedIds: string[]): Promise<void> {
    await Promise.all(orderedIds.map((id, index) =>
        client.from('habits').update({ sort_order: index }).eq('id', id).eq('user_id', userId)
    ));
}

export async function getNextHabitSortOrder(client: SupabaseClient, userId: string, cycleId: string): Promise<number> {
    const { data } = await client
        .from('habits').select('sort_order')
        .eq('cycle_id', cycleId).eq('user_id', userId)
        .order('sort_order', { ascending: false }).limit(1);
    return (data?.[0]?.sort_order ?? -1) + 1;
}
